import type { ReactElement } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';
import {
  ORGANIZATION_SCOPE_LABEL,
  securityGroupLabel,
} from './security-groups';

/**
 * Alerts-table cell showing the security group(s) an alert spans (KIR-3800).
 *
 * Reads `alert.securityGroups` straight from the API — the same field the
 * server sorts and filters on — so what the cell shows can never disagree with
 * the ordering or the active filter.
 *
 * One group renders as a chip; several render as "N groups" with the list in a
 * tooltip, because an alert deduplicates occurrences across users who may sit in
 * different groups. The per-occurrence breakdown lives in Show Instances.
 *
 * `undefined` means the field was not returned at all (flag off) and renders as
 * a dash — distinct from "ungrouped", which is a real answer.
 *
 * The array always lists every scope the alert spans, including an explicit
 * "Organization" entry whenever any occurrence was produced outside a group. So
 * a fully-ungrouped alert arrives as `[Organization]` and a partially-ungrouped
 * one as `[<group>, Organization]` — the cell can never claim a single group for
 * an alert the Organization filter also matches. The empty-array branch below is
 * therefore defensive only.
 */
export function AlertSecurityGroupsCell({
  alert,
}: {
  alert: GroupedAlert;
}): ReactElement {
  const groups = alert.securityGroups;

  if (groups === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }

  if (groups.length === 0) {
    return (
      <Badge variant="outline" className="max-w-full truncate">
        {ORGANIZATION_SCOPE_LABEL}
      </Badge>
    );
  }

  if (groups.length === 1) {
    return (
      <Badge variant="outline" className="max-w-full truncate">
        {securityGroupLabel(groups[0])}
      </Badge>
    );
  }

  // Labelled, not raw names: a soft-deleted group and a live one can share a
  // name, and an unlabelled tooltip would list the same string twice with
  // nothing to tell them apart.
  const names = groups.map(securityGroupLabel);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-default truncate underline decoration-dotted underline-offset-2">
          {names.length} groups
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-sm whitespace-pre-line">
        {names.join('\n')}
      </TooltipContent>
    </Tooltip>
  );
}

AlertSecurityGroupsCell.displayName = 'AlertSecurityGroupsCell';
